import { useState, useEffect } from "react";
import {
  X,
  DollarSign,
  CreditCard,
  Banknote,
  Check,
  Building2,
  Plus,
  Trash2,
} from "lucide-react";
import medicalDB from "../services/database";

const TransactionForm = ({
  isOpen,
  onClose,
  onSave,
  patientId,
  initialData = null,
}) => {
  const [formData, setFormData] = useState({
    items: [{ name: "", price: 0, quantity: 1 }],
    totalAmount: 0,
    paidAmount: 0,
    paymentMethod: medicalDB.PAYMENT_METHODS?.CASH || "cash",
    notes: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState(null);

  // Payment method options
  const paymentMethods = [
    {
      value: medicalDB.PAYMENT_METHODS?.CASH || "cash",
      label: "Cash",
      icon: Banknote,
    },
    {
      value: medicalDB.PAYMENT_METHODS?.CARD || "card",
      label: "Card",
      icon: CreditCard,
    },
    {
      value: medicalDB.PAYMENT_METHODS?.CHECK || "check",
      label: "Check",
      icon: Check,
    },
    {
      value: medicalDB.PAYMENT_METHODS?.BANK_TRANSFER || "bank_transfer",
      label: "Bank Transfer",
      icon: Building2,
    },
  ];

  useEffect(() => {
    if (isOpen && patientId) {
      loadPatient();
    }

    if (initialData) {
      setFormData({
        ...initialData,
        date: new Date(initialData.date).toISOString().split("T")[0],
      });
    } else {
      // Reset form
      setFormData({
        items: [{ name: "", price: 0, quantity: 1 }],
        totalAmount: 0,
        paidAmount: 0,
        paymentMethod: medicalDB.PAYMENT_METHODS?.CASH || "cash",
        notes: "",
        date: new Date().toISOString().split("T")[0],
      });
    }
  }, [isOpen, patientId, initialData]);

  // Recalculate total when items change
  useEffect(() => {
    const total = formData.items.reduce(
      (sum, item) =>
        sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1),
      0
    );
    setFormData((prev) => ({
      ...prev,
      totalAmount: total,
      paidAmount: prev.paidAmount || total, // Update paid amount if it's 0 or matches old total
    }));
  }, [formData.items]);

  const loadPatient = async () => {
    try {
      const patientData = await medicalDB.getPatient(patientId);
      setPatient(patientData);
    } catch (error) {
      console.error("Error loading patient:", error);
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { name: "", price: 0, quantity: 1 }],
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
    }
  };

  const updateItem = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.items.some((item) => item.name.trim())) {
      alert("Please add at least one item with a name");
      return;
    }

    try {
      setLoading(true);

      const transactionData = {
        patientId,
        date: new Date(formData.date),
        items: formData.items.filter((item) => item.name.trim()),
        totalAmount: formData.totalAmount,
        paidAmount: formData.paidAmount,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
      };

      if (initialData) {
        // Update existing transaction
        await medicalDB.updateTransaction(initialData.id, transactionData);
      } else {
        // Create new transaction
        await medicalDB.createTransaction(transactionData);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving transaction:", error);
      alert("Error saving transaction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const balance = formData.totalAmount - formData.paidAmount;
  const change = Math.max(0, formData.paidAmount - formData.totalAmount);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <DollarSign className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {initialData ? "Edit Transaction" : "Record Payment"}
              </h2>
              {patient && (
                <p className="text-sm text-gray-500">Patient: {patient.name}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]"
        >
          <div className="space-y-6">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, date: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Services/Items
                </label>
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Item
                </button>
              </div>

              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Service or item name"
                        value={item.name}
                        onChange={(e) =>
                          updateItem(index, "name", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        placeholder="Qty"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(
                            index,
                            "quantity",
                            parseInt(e.target.value) || 1
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                      />
                    </div>
                    <div className="w-32 flex items-center gap-1">
                      <span className="text-gray-500">₪</span>
                      <input
                        type="number"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) =>
                          updateItem(
                            index,
                            "price",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                      />
                    </div>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Total Amount */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between items-center text-lg font-semibold text-blue-900">
                <span>Total Amount:</span>
                <span>₪{formData.totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Payment Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                {paymentMethods.map((method) => {
                  const IconComponent = method.icon;
                  return (
                    <button
                      key={method.value}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          paymentMethod: method.value,
                        }))
                      }
                      className={`flex items-center gap-2 p-3 border rounded-lg transition-colors ${
                        formData.paymentMethod === method.value
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <IconComponent className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {method.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Payment Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount Paid
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">₪</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.paidAmount}
                  onFocus={(e) => {
                    // Clear the field if it's 0
                    if (parseFloat(e.target.value) === 0) {
                      e.target.select();
                    }
                  }}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      paidAmount: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            {/* Change/Balance */}
            {change > 0 && (
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg text-green-700 font-medium">
                <span>Change:</span>
                <span>₪{change.toFixed(2)}</span>
              </div>
            )}

            {balance > 0 && (
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg text-orange-700 font-medium">
                <span>Outstanding Balance:</span>
                <span>₪{balance.toFixed(2)}</span>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Payment notes, special instructions, etc."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || formData.totalAmount <= 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <DollarSign className="h-4 w-4" />
                {initialData ? "Update Transaction" : "Record Payment"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionForm;
